(function () {
  'use strict';

  const MINUTE = 60000;
  const DAY = 86400000;
  const formatters = new Map();
  const displayFormatters = new Map();
  const dateOnlyFormatters = new Map();
  const timestamps = new Map();

  function calendarParts(date, time) {
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
    const timeMatch = /^(\d{2}):(\d{2})$/.exec(time || '');
    if (!dateMatch || !timeMatch) throw new Error('Unesite datum GGGG-MM-DD i vreme HH:mm.');
    const parts = [...dateMatch.slice(1), ...timeMatch.slice(1)].map(Number);
    const [year, month, day, hour, minute] = parts;
    const stamp = Date.UTC(year, month - 1, day, hour, minute);
    const check = new Date(stamp);
    if (year < 1970 || month < 1 || month > 12 || day < 1 || hour > 23 || minute > 59 ||
        check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 ||
        check.getUTCDate() !== day) {
      throw new Error('Datum ili vreme nisu ispravni.');
    }
    return parts;
  }

  function zoneFormatter(timeZone) {
    if (!formatters.has(timeZone)) {
      try {
        formatters.set(timeZone, new Intl.DateTimeFormat('en-GB', {
          timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
        }));
      } catch (error) {
        throw new Error('Izabrana vremenska zona nije ispravna.');
      }
    }
    return formatters.get(timeZone);
  }

  function zonedParts(stamp, timeZone) {
    const values = {};
    zoneFormatter(timeZone).formatToParts(new Date(stamp)).forEach((part) => {
      if (part.type !== 'literal') values[part.type] = Number(part.value);
    });
    return [values.year, values.month, values.day, values.hour, values.minute, values.second];
  }

  function wallStamp(parts) {
    return Date.UTC(parts[0], parts[1] - 1, parts[2], parts[3], parts[4], parts[5] || 0);
  }

  function zonedTimestamp(date, time, timeZone) {
    const key = `${timeZone}|${date}|${time}`;
    if (timestamps.has(key)) return timestamps.get(key);
    const desired = calendarParts(date, time);
    const naive = wallStamp(desired);
    // Sample both sides of a DST change, then verify candidates against the zone.
    // No browser-local Date constructor or fixed summer/winter offset is used.
    const offsets = new Set();
    [-36, -12, 0, 12, 36].forEach((hours) => {
      const probe = naive + hours * 3600000;
      offsets.add(wallStamp(zonedParts(probe, timeZone)) - probe);
    });
    const matches = [];
    offsets.forEach((offset) => {
      const candidate = naive - offset;
      const actual = zonedParts(candidate, timeZone);
      if (desired.every((value, index) => actual[index] === value)) matches.push(candidate);
    });
    if (!matches.length) {
      throw new Error('Ovo vreme ne postoji zbog prelaska na letnje računanje vremena. Izaberite drugo vreme.');
    }
    // During the repeated autumn hour use the later occurrence consistently.
    const result = Math.max(...matches);
    if (timestamps.size >= 64) timestamps.delete(timestamps.keys().next().value);
    timestamps.set(key, result);
    return result;
  }

  function isoTimestamp(value) {
    // Shopify date_time metafields may render as ISO (T/+01:00) or as the
    // Liquid DateTime form (space/+0100). Both retain an explicit timezone.
    const match = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?\s*(Z|UTC|[+-]\d{2}:?\d{2})$/i.exec((value || '').trim());
    if (!match) throw new Error('Datum proizvoda mora da sadrži vreme i vremensku zonu.');
    const parts = calendarParts(match[1], match[2]);
    const seconds = Number(match[3] || 0);
    if (seconds > 59) throw new Error('Datum proizvoda nije ispravan.');
    const milliseconds = Number((match[4] || '').padEnd(3, '0').slice(0, 3));
    const zone = match[5];
    let offset = 0;
    if (zone.toUpperCase() !== 'Z' && zone.toUpperCase() !== 'UTC') {
      const hours = Number(zone.slice(1, 3));
      const minutes = Number(zone.replace(':', '').slice(3, 5));
      if (hours > 14 || minutes > 59 || (hours === 14 && minutes !== 0)) {
        throw new Error('Vremenska zona datuma proizvoda nije ispravna.');
      }
      offset = (hours * 60 + minutes) * (zone[0] === '-' ? -1 : 1);
    }
    return wallStamp(parts) + seconds * 1000 + milliseconds - offset * MINUTE;
  }

  function resolveSchedule(config) {
    const timeZone = config.timeZone || 'Europe/Belgrade';
    zoneFormatter(timeZone);
    const end = config.endIso
      ? isoTimestamp(config.endIso.trim())
      : zonedTimestamp(config.endDate, config.endTime, timeZone);
    const start = config.startDate
      ? zonedTimestamp(config.startDate, config.startTime || '00:00', timeZone)
      : null;
    if (start !== null && start >= end) throw new Error('Početak akcije mora da bude pre završetka.');
    let repeatDays = 0;
    if (config.repeatEnabled === 'true' || config.repeatEnabled === true) {
      repeatDays = config.repeatDays == null || config.repeatDays === '' ? 30 : Number(config.repeatDays);
      if (!Number.isInteger(repeatDays) || repeatDays < 1 || repeatDays > 30) {
        throw new Error('Trajanje novog ciklusa mora biti ceo broj od 1 do 30 dana.');
      }
    }
    return { start, end, timeZone, repeatDays };
  }

  function cycleWindow(schedule, now) {
    if (!Number.isFinite(schedule.end) || !Number.isFinite(now) || !schedule.repeatDays || now < schedule.end) {
      return { start: schedule.start, end: schedule.end };
    }
    // Anchor every cycle to the saved first deadline, never to a page load or
    // the latest tick. A hidden tab or a new visitor joins the same current cycle.
    // Each configured day is exactly 24 hours, including across DST changes.
    const duration = schedule.repeatDays * DAY;
    const elapsedCycles = Math.floor((now - schedule.end) / duration);
    const start = schedule.end + elapsedCycles * duration;
    return { start, end: start + duration };
  }

  function cycleEnd(schedule, now) {
    return cycleWindow(schedule, now).end;
  }

  function remainingTime(end, now) {
    const total = Number.isFinite(end) && Number.isFinite(now)
      ? Math.max(0, Math.ceil((end - now) / 1000)) : 0;
    return {
      days: Math.floor(total / 86400),
      hours: Math.floor((total % 86400) / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60,
      expired: !Number.isFinite(end) || !Number.isFinite(now) || end <= now
    };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { calendarParts, zonedTimestamp, isoTimestamp, resolveSchedule, remainingTime, cycleWindow, cycleEnd };
  }
  if (typeof window === 'undefined' || !window.customElements ||
      window.customElements.get('sale-countdown')) return;

  const instances = new Set();
  let clock = null;

  function stopIfIdle() {
    if (instances.size || clock === null) return;
    window.clearInterval(clock);
    clock = null;
  }

  function tick() {
    const now = Date.now();
    instances.forEach((timer) => timer.update(now));
    stopIfIdle();
  }

  function startClock() {
    if (clock === null && instances.size && !document.hidden) {
      clock = window.setInterval(tick, 1000);
    }
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (clock !== null) window.clearInterval(clock);
      clock = null;
    } else {
      tick();
      startClock();
    }
  });
  window.addEventListener('pageshow', () => { tick(); startClock(); });

  class SaleCountdown extends HTMLElement {
    connectedCallback() {
      // Imported facet/Infinity markup and editor sections may connect before
      // all children are ready; hydrate after the current DOM operation.
      const generation = (this.generation || 0) + 1;
      this.generation = generation;
      Promise.resolve().then(() => {
        if (!this.isConnected || this.generation !== generation) return;
        this.initialize();
      });
    }

    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      instances.delete(this);
      stopIfIdle();
    }

    initialize() {
      instances.delete(this);
      this.editor = this.dataset.designMode === 'true';
      this.clockElement = this.querySelector('[data-sale-clock]');
      this.endedElement = this.querySelector('[data-sale-ended]');
      this.errorElement = this.querySelector('[data-sale-error]');
      this.dateRangeElement = this.querySelector('[data-sale-date-range]');
      this.values = {};
      ['days', 'hours', 'minutes', 'seconds'].forEach((unit) => {
        this.values[unit] = this.querySelector(`[data-sale-value="${unit}"]`);
      });
      try {
        this.schedule = resolveSchedule(this.dataset);
        this.deadlineElement = this.querySelector('[data-sale-deadline]');
        this.displayedEnd = null;
        if (!displayFormatters.has(this.schedule.timeZone)) {
          displayFormatters.set(this.schedule.timeZone, new Intl.DateTimeFormat('sr-Latn-RS', {
            timeZone: this.schedule.timeZone, day: 'numeric', month: 'long',
            year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
          }));
        }
        if (!dateOnlyFormatters.has(this.schedule.timeZone)) {
          dateOnlyFormatters.set(this.schedule.timeZone, new Intl.DateTimeFormat('sr-Latn-RS', {
            timeZone: this.schedule.timeZone, day: '2-digit', month: '2-digit', year: 'numeric'
          }));
        }
        const format = displayFormatters.get(this.schedule.timeZone);
        this.formatDate = (stamp) => format.format(new Date(stamp));
        const formatDateOnly = dateOnlyFormatters.get(this.schedule.timeZone);
        this.formatDateOnly = (stamp) => formatDateOnly.format(new Date(stamp));
        this.displayedDateRange = null;
        instances.add(this);
        this.update(Date.now());
        startClock();
      } catch (error) {
        this.showHint('invalid', error.message);
        stopIfIdle();
      }
    }

    showHint(state, message) {
      this.dataset.saleState = state;
      this.hidden = !(this.editor && this.errorElement);
      if (this.clockElement) this.clockElement.hidden = true;
      if (this.endedElement) this.endedElement.hidden = true;
      if (this.errorElement) {
        this.errorElement.textContent = message;
        this.errorElement.hidden = !this.editor;
      }
    }

    update(now) {
      if (!this.isConnected) {
        instances.delete(this);
        return;
      }
      const window = cycleWindow(this.schedule, now);
      const end = window.end;
      if (end !== this.displayedEnd) {
        this.displayedEnd = end;
        if (this.deadlineElement) {
          this.deadlineElement.setAttribute('datetime', new Date(end).toISOString());
          this.deadlineElement.textContent = this.formatDate(end);
        }
      }
      if (this.dateRangeElement) {
        const rangeKey = `${window.start == null ? '' : window.start}|${end}`;
        if (rangeKey !== this.displayedDateRange) {
          this.displayedDateRange = rangeKey;
          let rangeText;
          if (Number.isFinite(window.start)) {
            rangeText = `Akcija traje od ${this.formatDateOnly(window.start)} do ${this.formatDateOnly(end)}`;
            this.dateRangeElement.dataset.saleDateStart = new Date(window.start).toISOString();
          } else {
            rangeText = `Akcija traje do ${this.formatDateOnly(end)}`;
            this.dateRangeElement.removeAttribute('data-sale-date-start');
          }
          this.dateRangeElement.dataset.saleDateEnd = new Date(end).toISOString();
          this.dateRangeElement.textContent = rangeText;
          this.dateRangeElement.hidden = false;
        }
      }
      if (this.schedule.start !== null && now < this.schedule.start) {
        this.showHint('scheduled', `Akcija počinje ${this.formatDate(this.schedule.start)}.`);
        return;
      }
      if (this.errorElement) this.errorElement.hidden = true;
      const remaining = remainingTime(end, now);
      if (remaining.expired) {
        this.dataset.saleState = 'expired';
        const showMessage = this.classList.contains('si-sale-timer--banner') &&
          this.dataset.expiry === 'message' && this.endedElement;
        this.hidden = !showMessage;
        if (this.clockElement) this.clockElement.hidden = true;
        if (this.endedElement) this.endedElement.hidden = !showMessage;
        instances.delete(this);
        return;
      }
      this.dataset.saleState = 'active';
      this.hidden = false;
      if (this.clockElement) this.clockElement.hidden = false;
      if (this.endedElement) this.endedElement.hidden = true;
      Object.keys(this.values).forEach((unit) => {
        const node = this.values[unit];
        const nextValue = String(remaining[unit]).padStart(2, '0');
        if (node && node.textContent !== nextValue) node.textContent = nextValue;
      });
    }
  }

  window.customElements.define('sale-countdown', SaleCountdown);
}());
